import React, { useMemo, useState, useEffect, useRef } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { reportTypeService } from '../../services';
import { useAuth } from '../../context/AuthContext';
import { formatReportTypeDisplayName, getIconClass as designGetIconClass, getIconLetter as designGetIconLetter } from '../../models/ReportTypeDesignHelper';
import { ReportTypeFavorites } from '../../models/ReportTypeFavorites';
import { isElectron, isCapacitor } from '../../utils/platform';
import './reportTypeCompact.css';

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
export function getButtonClass(typeName) {
  // Handle cases where typeName is not a string
  if (!typeName || typeof typeName !== 'string') {
    return 'button-info';
  }
  
  const lower = typeName.toLowerCase();
  switch (lower) {
    case 'consultation':    return 'button-blue';
    case 'note':            return 'button-yellow';
    case 'lettre':          return 'button-green';
    case 'texte':           return 'button-orange';
    case 'mail':            return 'button-cyan';
    case 'traduction':      return 'button-pink';
    case 'examen':          return 'button-purple';
    case 'liste':           return 'button-cyan';
    case 'diagnostic':      return 'button-mint';
    case 'transmission':    return 'button-indigo';
    case 'entree':          return 'button-brown';
    case 'synthèse':
    case 'echocardiographie':
    case 'ergometrie':
    case 'coronarographie':
    case 'ecg':             return 'button-pink';
    case 'inconnu':         return 'button-red';
    default:                return 'button-info';
  }
}

function getIconClass(typeName) {
  return designGetIconClass(typeName);
}

function getIconLetter(typeName) {
  return designGetIconLetter(typeName);
}



// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────
export default function ReportTypeSelector({
  reportTypes = [],
  onSelect,
  onReportTypesChange,
  liveMode = false
}) {
  // ─── State ───────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [favoritesModel, setFavoritesModel] = useState(null);
  const [favoriteNames, setFavoriteNames] = useState([]);
  const { profile } = useAuth();
  const searchInputRef = useRef(null);

  // Focus search input on mount when not in Capacitor
  useEffect(() => {
    if (!isCapacitor && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Load favorites from service with profile fallback
  useEffect(() => {
    let cancelled = false;
    async function loadFavorites() {
      // Pre-hydrate from local profile favorites so UI shows immediately
      try {
        const pre = profile?.reportTypeFavorites;
        if (!cancelled && pre instanceof ReportTypeFavorites) {
          setFavoritesModel(pre);
          setFavoriteNames(pre.getFavorites());
        }
      } catch (_) {}

      setIsLoadingTypes(true);
      try {
        const favorites = await reportTypeService.getFavorites();
        if (!cancelled) {
          setFavoritesModel(favorites);
          setFavoriteNames(favorites.getFavorites());
        }
      } catch (err) {
        const fallback = profile?.reportTypeFavorites;
        if (!cancelled) {
          if (fallback instanceof ReportTypeFavorites) {
            setFavoritesModel(fallback);
            setFavoriteNames(fallback.getFavorites());
          } else {
            setFavoritesModel(null);
            setFavoriteNames([]);
          }
        }
      } finally {
        if (!cancelled) setIsLoadingTypes(false);
      }
    }

    loadFavorites();
    return () => {
      cancelled = true;
    };
  }, [refreshKey, profile]);

  // Compute effective favorites from loaded names
  const effectiveFavorites = useMemo(() => {
    return favoriteNames;
  }, [favoriteNames]);

  // Filtered names based on search
  const filteredFavorites = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return effectiveFavorites;
    return effectiveFavorites.filter(name => name.toLowerCase().includes(q));
  }, [effectiveFavorites, search]);

  // ─── Rendu ───────────────────────────────────────────────
  return (
    <div className="report-type-container">
      {/* Search Section with Enterprise Icon */}
      <div className="report-type-search-container">
        <div className="report-type-search-wrapper">
          <div className="report-type-search-input-wrapper">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Rechercher un modèle..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="report-type-search"
              autoFocus={!isCapacitor}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                title="Effacer la recherche"
                className="report-type-clear-search"
              >
                ×
              </button>
            )}
          </div>
          
        </div>
      </div>


      {/* Content Area */}
      <div className={`report-type-content ${liveMode ? 'report-type-content-live' : ''}`}>
        {/* Loading State - only if no locally available favorites */}
        {isLoadingTypes && effectiveFavorites.length === 0 && (
          <div className="report-type-loading">
            <FaSpinner className="fa-spin" size={24} />
            <span style={{ marginLeft: 8 }}>
              Chargement des types de rapport...
            </span>
          </div>
        )}

        {/* Empty State */}
        {!isLoadingTypes && filteredFavorites.length === 0 && (
          <div className="report-type-empty">
            <p>
              {search 
                ? `Aucun template trouvé pour "${search}"`
                : 'Aucun template disponible'
              }
            </p>
          </div>
        )}

        {/* Favorites grid (names) - visible even while loading */}
        {filteredFavorites.length > 0 && (
          <div className="report-type-category">
            <div
              className={`report-type-grid ${liveMode ? 'report-type-grid-live' : ''}`}
              style={liveMode ? { gridTemplateColumns: 'repeat(auto-fill, 70px)', justifyContent: 'center' } : {}}
            >
              {filteredFavorites.map((typeName) => (
                <div
                  key={`favorites-${typeName}`}
                  className={`report-type-item ${liveMode ? 'report-type-item-live' : ''}`}
                  onClick={() => onSelect(typeName)}
                >
                  <button
                    className={`report-type-button ${liveMode ? 'report-type-button-live' : ''}`}
                    style={liveMode ? { width: 70, minWidth: 70, maxWidth: 70, height: 50, padding: 0, boxSizing: 'border-box', overflow: 'hidden' } : {}}
                  >
                    {!liveMode && (
                      <span className={`report-type-icon ${getIconClass(typeName)}`}>
                        {getIconLetter(typeName)}
                      </span>
                    )}
                    <div className="report-type-text" style={liveMode ? { width: '100%' } : {}}>
                      <span className="report-type-name" style={liveMode ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%', textAlign: 'center', fontSize: 12, lineHeight: 1 } : {}}>
                        {formatReportTypeDisplayName(typeName)}
                      </span>
                      {/* Description removed */}
                    </div>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}